import { useMemo } from "react";

import ConfiguracionNovedadesHorasExtra from "@components/Configuracion/Novedades/ConfiguracionNovedadesHorasExtra";
import { QontyLoading } from "@components/QontyLoading";
import { NovedadesExportAnalyticsHelper } from "@core/Analytics/NovedadesExportAnalyticsHelper";
import { Application } from "@core/Application";
import { NovedadesExportService } from "@services/NovedadesExportService";
import { NovedadesService } from "@services/NovedadesService";
import { HollowedButton, NormalButton } from "@shared/designSystem/Button";
import { Modal } from "@shared/designSystem/messages/Modal";
import { ToastManager } from "@shared/designSystem/messages/ToastManager";
import { TabScreenSleek } from "@shared/designSystem/TabScreen";
import { TextInput } from "@shared/designSystem/TextInput";
import { SmallText, Text } from "@shared/designSystem/Texts";
import { Flap } from "@shared/models/Flap";

import { useExportNovedadesConfigurationModalViewModel } from "./ExportNovedadesConfigurationModalViewModel";
import { designSystemStyles } from "@shared/styles/designSystemStyles";
import { HorizontalContainer, VerticalContainer } from "@shared/designSystem/Containers";
import ExportNovedadesStatusMappingConfigurationScreen from "./ExportNovedadesStatusMappingConfigurationScreen";

interface ExportNovedadesConfigurationModalScreenProps {
    isOpen: boolean;
    orgID: string;
    onClose: () => void;
}

const ExportNovedadesConfigurationModalScreen: React.FC<ExportNovedadesConfigurationModalScreenProps> = ({
    isOpen,
    orgID,
    onClose,
}) => {
    const exportService = useMemo(() => {
        return new NovedadesExportService(Application.shared().coreDependencies.httpClient);
    }, []);
    const novedadesService = useMemo(() => {
        return new NovedadesService(Application.shared().coreDependencies.httpClient);
    }, []);
    const viewModel = useExportNovedadesConfigurationModalViewModel({
        isOpen,
        orgID,
        exportService,
    });
    const setSelectedTab = (tabID: string) => {
        viewModel.setSelectedTab(tabID);
        if (tabID === "statusMapping" || tabID === "extraHours" || tabID === "colppy") {
            NovedadesExportAnalyticsHelper.trackConfigurationTabSelected(tabID);
        }
    };

    const handleColppySave = async () => {
        const didSave = await viewModel.saveColppyConfiguration();
        if (didSave) {
            ToastManager.shared().showSuccessMessage("Configuración guardada.".localized());
        } else {
            ToastManager.shared().showErrorMessage("No se pudo guardar la configuración.".localized());
        }
    };

    const renderColppyConfiguration = () => {
        if (viewModel.colppyLoading) {
            return (
                <VerticalContainer height="100%" alignment="center" justifyContent="center">
                    <QontyLoading text={"Cargando configuración...".localized()} />
                </VerticalContainer>
            );
        }

        return (
            <VerticalContainer
                width="100%"
                height="100%"
                gap={designSystemStyles.gapLg}
                alignment="stretch"
            >
                <VerticalContainer gap={designSystemStyles.gapXxxSm} margin="0" width="360px">
                    <TextInput
                        label={"Código de empresa en Colppy".localized()}
                        value={viewModel.colppyCode}
                        onChange={viewModel.setColppyCode}
                        disabled={viewModel.colppySaving}
                    />
                    <SmallText
                        text={"Es el código de empresa que se muestra arriba a la izquierda en la pantalla de inicio de Colppy eSueldos. Si queda vacío, se usará el ID de la organización como marcador en el archivo exportado.".localized()}
                        color={designSystemStyles.colorTextSecondary}
                    />
                </VerticalContainer>

                {viewModel.colppyError && (
                    <Text text={viewModel.colppyError} color={designSystemStyles.colorError} />
                )}

                <HorizontalContainer justifyContent="flex-end" gap={designSystemStyles.gapMd}>
                    <HollowedButton
                        text={"Cancelar".localized()}
                        onClick={viewModel.cancelColppy}
                        disabled={!viewModel.hasColppyChanges || viewModel.colppySaving}
                        width="160px"
                    />
                    <NormalButton
                        text={viewModel.colppySaving ? "Guardando...".localized() : "Guardar".localized()}
                        onClick={() => void handleColppySave()}
                        disabled={!viewModel.hasColppyChanges || viewModel.colppySaving}
                        loading={viewModel.colppySaving}
                        width="160px"
                    />
                </HorizontalContainer>
            </VerticalContainer>
        );
    };

    const flaps: Flap[] = [
        {
            id: "statusMapping",
            label: "Columnas".localized(),
            content:
                <VerticalContainer
                    paddingTop={designSystemStyles.paddingLg}
                    height="100%"
                >
                    <ExportNovedadesStatusMappingConfigurationScreen
                        orgID={orgID}
                        exportService={exportService}
                        novedadesService={novedadesService}
                    />
                </VerticalContainer>,
        },
        {
            id: "extraHours",
            label: "Horas extra".localized(),
            content:
                <VerticalContainer
                    paddingTop={designSystemStyles.paddingLg}
                    height="100%"
                    scrollable={true}
                >
                    <ConfiguracionNovedadesHorasExtra
                        orgID={orgID}
                    />
                </VerticalContainer>,
        },
        {
            id: "colppy",
            label: "Colppy".localized(),
            content:
                <VerticalContainer
                    paddingTop={designSystemStyles.paddingLg}
                    height="100%"
                >
                    {renderColppyConfiguration()}
                </VerticalContainer>,
        },
    ];

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={"Configuración de exportación".localized()}
            width="980px"
            height="720px"
            maxWidth="94vw"
            maxHeight="88vh"
        >
            <TabScreenSleek
                title={"Configuración de exportación".localized()}
                flaps={flaps}
                clickedScreen={viewModel.selectedTab}
                setClickedScreen={setSelectedTab}
            />
        </Modal>
    );
};

export default ExportNovedadesConfigurationModalScreen;
